import React, {Fragment, useCallback, useEffect, useState} from "react";
import {Box, FormControlLabel, Switch} from "@mui/material";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import useForceUpdate from "../../hooks/useForceUpdate";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import DatasetFilterAttributeIcon from "../dataset-filter-attribute-icon";
import {LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH} from "../label-format-selector/constants";
import MaterialTable from "../material-table";
import MaterialTree, {DEFAULT_SELECT_MODE, SINGLE_SELECT_MODE} from "../material-tree";
import {getFormattedDimensionValueLabel, MARGINAL_DIMENSION_KEY} from "../../utils/dataset";
import {getTextWidth} from "../../utils/style";
import {getFilteredTreeWithPaths, getMappedTree} from "../../utils/tree";
import {isMultiSelectDim} from "./utils";

const $ = window.jQuery;

const alertStyle = {
  marginBottom: "8px"
};

const DatasetFilters = ({
  datasetId: externalDatasetId,
  jsonStat,
  timeDim,
  layout,
  labelFormat,
  getIsDimensionAllowed,
  getDimensionLabel,
  getFilterValues,
  getTreeFilterValues,
  getDimensionIsHierarchical,
  getFormattedName,
  getFormattedTooltip,
  dimAttributeMap,
  onSelect,
  onCriteriaShow,
  getIsDatasetExceedingLimit,
  enclosingContainerId,
  localizedTimePeriodFormatMapExternal
}) => {
  const {t} = useTranslation();

  const forceUpdate = useForceUpdate();

  const [visibleId, setVisibleId] = useState(null);
  const [data, setData] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState(null);
  const [defaultCheckedKeys, setDefaultCheckedKeys] = useState(null);
  const [checkedKeysSnapshot, setCheckedKeysSnapshot] = useState(null);
  const [dataBackup, setDataBackup] = useState(null);

  useEffect(() => {
    if (!defaultCheckedKeys && checkedKeys) {
      setDefaultCheckedKeys(checkedKeys);
    } else if (defaultCheckedKeys && !checkedKeys) {
      setDefaultCheckedKeys(null);
    }
  }, [checkedKeys, defaultCheckedKeys]);

  useEffect(() => {
    return () => {
      $("#dataset-filters__text-width-el").remove();
    };
  }, []);

  useEffect(() => {
    window.addEventListener("resize", forceUpdate);
    return () => window.removeEventListener("resize", forceUpdate);
  }, [forceUpdate]);

  const filters = (layout?.primaryDim || []).concat(layout?.secondaryDim || []).concat(layout?.filters || []);

  const handleFilterOpen = visibleIdx => {
    setVisibleId(visibleIdx);
    const dim = filters[visibleIdx];
    const dimIsHierarchical = getDimensionIsHierarchical(dim);

    if (dimIsHierarchical) {
      const data = [...getTreeFilterValues(dim)];
      setData(data);
    } else {
      const data = [...getFilterValues(dim)];
      if (dim === timeDim) {
        data.reverse();
      }
      setData(data.map(val => ({id: val})));
    }

    if (isMultiSelectDim(dim, layout)) {
      if (dim === layout?.primaryDim?.[0]) {
        setCheckedKeys(layout.primaryDimValues);
      } else if (dim === layout?.secondaryDim?.[0]) {
        setCheckedKeys(layout.secondaryDimValues);
      }
    } else {
      setCheckedKeys([layout.filtersValue[dim]]);
    }
  };

  const handleFilterClose = () => {
    setVisibleId(null);
    setData(null);
    setCheckedKeys(null);
    setDefaultCheckedKeys(null);
    setCheckedKeysSnapshot(null);
    setDataBackup(null);
  };

  const handleFilterSubmit = (dimension, values) => {
    const submitValues = isMultiSelectDim(dimension, layout)
      ? values
      : getDimensionIsHierarchical(filters[visibleId])
        ? values[0]
        : values;
    onSelect(dimension, submitValues);
    handleFilterClose();
  };

  const showOnlySelectedSwitch = useCallback(() => {
    const dim = filters[visibleId];
    if (dim === timeDim) {
      return false;
    }
    const hasData = (data || []).length > 1 || (data?.[0]?.children || []).length > 0;
    return checkedKeys !== null || hasData;
  }, [checkedKeys, data, filters, visibleId, timeDim]);

  const onShowOnlySelectedSwitchClick = useCallback(
    checked => {
      let newData;
      if (checked) {
        const orData = _.cloneDeep(data);
        newData = getFilteredTreeWithPaths(orData, "children", ({id}) => checkedKeys.includes(id));
        newData = getMappedTree(newData, "children", node => ({
          ...node,
          isSelectable: node.isSelectable && checkedKeys.includes(node.id)
        }));
        setDataBackup(orData);
      } else {
        newData = dataBackup;
        setDataBackup(null);
      }
      setData(newData);
      setCheckedKeys(checkedKeys);
      setDefaultCheckedKeys(checkedKeys);
      setCheckedKeysSnapshot(checked ? checkedKeys : null);
    },
    [checkedKeys, data, dataBackup]
  );

  return (
    <Fragment>
      <Box sx={{maxWidth: "100%"}}>
        {filters.map((dim, idx) => {
          const isDimensionAllowed = getIsDimensionAllowed(dim);
          if (!isDimensionAllowed) {
            return null;
          } else {
            const {filtersValue} = layout;

            if ($("#dataset-filters__text-width-el").length === 0) {
              $("<span id='dataset-filters__text-width-el'>")
                .css({
                  visibility: "hidden",
                  fontSize: 16
                })
                .appendTo("body");
            }

            const dimLabel = getDimensionLabel(dim);
            const minWidth = getTextWidth(dimLabel, $("#dataset-filters__text-width-el").get(0)) + 1;
            const selectStyle = {minWidth: minWidth};

            if (enclosingContainerId) {
              const $containerElement = $(enclosingContainerId);
              if ($containerElement.length > 0) {
                const containerWidth = $containerElement.width() || 0;
                if (containerWidth > 0) {
                  selectStyle.maxWidth = containerWidth;
                }
              }
            }

            const value = isMultiSelectDim(dim, layout)
              ? layout.primaryDim[0] === dim
                ? layout.primaryDimValues
                : layout.secondaryDimValues
              : filtersValue[dim];

            const datasetId = externalDatasetId
              ? externalDatasetId
              : !isMultiSelectDim(dim, layout) &&
                  dim === MARGINAL_DIMENSION_KEY &&
                  jsonStat?.extension?.marginalvalues?.[value]?.datasetid
                ? jsonStat.extension.marginalvalues[value].datasetid
                : jsonStat?.extension?.datasets?.[0];

            const values = getFilterValues(dim);

            const getValueLabel = value =>
              isMultiSelectDim(dim, layout)
                ? values.length !== 1
                  ? t("components.datasetFilters.selectedCount", {count: value.length})
                  : getFormattedName(dim, value[0], labelFormat)
                : getFormattedName(dim, value, labelFormat);

            return (
              <Box
                key={idx}
                sx={{
                  display: "inline-block",
                  verticalAlign: "bottom",
                  marginRight: "16px",
                  marginBottom: "8px",
                  maxWidth: "100%"
                }}
              >
                {/* {isMultiSelectDim(dim, layout) && (
                  <InputLabel
                    sx={{
                      marginBottom: "4px",
                      fontSize: "13px",
                      color: theme => theme.palette.primary.main
                    }}
                  >
                    {layout.primaryDim[0] === dim
                      ? t("components.datasetFilters.primaryDim")
                      : t("components.datasetFilters.secondaryDim")}
                  </InputLabel>
                )} */}
                <FormControl
                  sx={{
                    display: "inline-block",
                    verticalAlign: "bottom",
                    maxWidth: "100%"
                  }}
                >
                  <InputLabel>{dimLabel}</InputLabel>
                  <Tooltip title={getValueLabel(value)}>
                    <Select
                      id={`dataset-filters__filter-select${dim}`}
                      open={false}
                      style={selectStyle}
                      value={value}
                      multiple={isMultiSelectDim(dim, layout)}
                      renderValue={value => getValueLabel(value)}
                      sx={{
                        fontSize: "14px",
                        maxWidth: "100%"
                      }}
                      onOpen={() => handleFilterOpen(idx)}
                      SelectDisplayProps={{
                        "aria-haspopup": true,
                        "aria-label": `${dimLabel}: ${getValueLabel(value)}`
                      }}
                      slotProps={{
                        input: {
                          "aria-labelledby": `dataset-filters__filter-select${dim}`
                        }
                      }}
                    >
                      {values.map((el, idx) => (
                        <MenuItem key={idx + el} value={el}>
                          {el}
                        </MenuItem>
                      ))}
                    </Select>
                  </Tooltip>
                </FormControl>
                {jsonStat && (
                  <div style={{display: "inline-block", marginBottom: 4}}>
                    <DatasetFilterAttributeIcon
                      dimension={dim}
                      dimensionValues={isMultiSelectDim(dim, layout) ? value : [value]}
                      datasetDimAttributeMap={dimAttributeMap?.[datasetId]}
                      labelFormat={labelFormat}
                      getDimValueLabel={dimVal =>
                        getFormattedDimensionValueLabel(
                          jsonStat,
                          datasetId,
                          dim,
                          dimVal,
                          labelFormat,
                          undefined,
                          localizedTimePeriodFormatMapExternal
                        )
                      }
                    />
                  </div>
                )}
              </Box>
            );
          }
        })}
      </Box>
      <CustomDialog open={data !== null} onClose={handleFilterClose} id={"dataset-filters-dialog"}>
        <CustomDialogTitle onClose={handleFilterClose} id={"dataset-filters-dialog"}>
          {visibleId !== null
            ? !isMultiSelectDim(filters[visibleId], layout)
              ? t("components.datasetFilters.modals.singleSelect", {
                  dimension: getDimensionLabel(filters[visibleId])
                })
              : t("components.datasetFilters.modals.multiSelect", {
                  dimension: getDimensionLabel(filters[visibleId])
                })
            : ""}
        </CustomDialogTitle>
        <DialogContent>
          {getIsDatasetExceedingLimit && getIsDatasetExceedingLimit(filters[visibleId], checkedKeys) && (
            <Alert severity="warning" sx={alertStyle}>
              {t("components.datasetFilters.alerts.limitExceeded")}
            </Alert>
          )}
          {onCriteriaShow && (
            <Alert severity="info" sx={alertStyle}>
              {t("components.datasetFilters.alerts.openCriteria.prefixLabel")}
              <Box
                component="span"
                sx={{
                  textDecoration: "underline",
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
                onClick={() => {
                  handleFilterClose();
                  onCriteriaShow(filters[visibleId]);
                }}
              >
                {t("components.datasetFilters.alerts.openCriteria.clickableLabel")}
              </Box>
              {t("components.datasetFilters.alerts.openCriteria.suffixLabel")}
            </Alert>
          )}
          {getDimensionIsHierarchical(filters[visibleId]) ? (
            <>
              {showOnlySelectedSwitch() && (
                <Box id="dataset-filters__only-selected-switch" sx={{textAlign: "right"}}>
                  <Tooltip title={t("components.criteria.codelistHeader.onlySelected.tooltip")}>
                    <FormControlLabel
                      label={t("components.criteria.codelistHeader.onlySelected.label")}
                      labelPlacement="start"
                      control={
                        <Switch
                          checked={checkedKeysSnapshot !== null}
                          onChange={({target}) => {
                            onShowOnlySelectedSwitchClick(target.checked);
                          }}
                          disabled={checkedKeysSnapshot === null && (checkedKeys || []).length === 0}
                        />
                      }
                    />
                  </Tooltip>
                </Box>
              )}
              <MaterialTree
                treeData={data}
                idKey={"id"}
                childrenKey={"children"}
                labelKey={"label"}
                isSelectableKey={"isSelectable"}
                selectMode={isMultiSelectDim(filters[visibleId], layout) ? DEFAULT_SELECT_MODE : SINGLE_SELECT_MODE}
                defaultSelectedNodeIds={defaultCheckedKeys}
                onNodeSelect={setCheckedKeys}
                defaultExpandedNodeIds={defaultCheckedKeys}
                height={400}
              />
            </>
          ) : !isMultiSelectDim(filters[visibleId], layout) ? (
            <Box sx={{height: "400px"}}>
              <MaterialTable
                data={data}
                idKey={"id"}
                columns={[
                  {
                    field: "id",
                    title: "",
                    render: ({id}) =>
                      getFormattedName(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH, true),
                    renderText: ({id}) =>
                      getFormattedName(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH),
                    renderTooltip: ({id}) =>
                      getFormattedTooltip(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH),
                    minWidth: 100
                  }
                ]}
                onRowClick={(_e, rowData) => handleFilterSubmit(filters[visibleId], rowData.id)}
                getRowStyle={rowData => ({
                  background: rowData.id === layout.filtersValue[filters[visibleId]] ? "#fff9e5" : undefined
                })}
                hideHeader
                compact
              />
            </Box>
          ) : (
            <Box sx={{height: "400px"}}>
              <MaterialTable
                data={data}
                idKey={"id"}
                columns={[
                  {
                    field: "id",
                    title: t("components.datasetFilters.table.columns.label.title"),
                    render: ({id}) =>
                      getFormattedName(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH, true),
                    renderText: ({id}) =>
                      getFormattedName(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH),
                    renderTooltip: ({id}) =>
                      getFormattedTooltip(filters[visibleId], id, LABEL_FORMAT_SELECTOR_LABEL_FORMAT_BOTH),
                    minWidth: 100
                  }
                ]}
                selectedRows={checkedKeys}
                onRowSelection={checkedKeys =>
                  setCheckedKeys(data.map(({id}) => id).filter(key => checkedKeys.includes(key)))
                }
                compact
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button id="dataset-filters__confirm-close-button" onClick={handleFilterClose} color="primary">
            {t("commons.confirm.close")}
          </Button>
          {(isMultiSelectDim(filters[visibleId], layout) || getDimensionIsHierarchical(filters[visibleId])) && (
            <Button
              id="dataset-filters__confirm-confirm-button"
              onClick={() => handleFilterSubmit(filters[visibleId], checkedKeys)}
              color="primary"
              disabled={
                (checkedKeys || []).length < 1 ||
                (getIsDatasetExceedingLimit && getIsDatasetExceedingLimit(filters[visibleId], checkedKeys))
              }
            >
              {t("commons.confirm.confirm")}
            </Button>
          )}
        </DialogActions>
      </CustomDialog>
    </Fragment>
  );
};

export default DatasetFilters;
